<!-- 
화면명 : UCDate.vue
작성일 : 2025-07-01
설명   : 달력 컨트롤
-->
<template>
  <div class="flex items-center space-x-2">
    <img src="@/assets/img/blue-circle.png" class="w-3 h-3" v-if="label" />

    <label class="text-blueGray-600 text-sm font-semibold whitespace-nowrap ">
      {{ label }}
    </label>

    <div class="flex items-center w-[150px] pr-0">
      <Datepicker
        :modelValue="parsedValue"
        @update:modelValue="onDateChange"
        :format="format"
        :disabled="disabled"
        :locale="locale"
        :ui="{
          input: `${
            isOpen ? 'ring-2 ring-lightBlue-500' : ''
          } focus:ring-2 focus:border-lightBlue-500 outline-none`,
        }"
        @open="isOpen = true"
        @closed="isOpen = false"
        :month-picker="monthPicker"
        :year-picker="yearPicker"
        auto-apply
        :cancel-text="null"
        :select-text="null"
        :clearable="false"
        :enable-time-picker="false"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, computed } from "vue";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

//import ko from "@vuepic/vue-datepicker/dist/locale/ko";
//import ko from "@vuepic/vue-datepicker/node_modules/date-fns/locale/ko";
//import ko from "date-fns/locale/ko/in dex.js";
import { format as formatDate } from "date-fns";

// props
const props = defineProps({
  label: { type: String, default: "" },
  modelValue: [Date, String, Object],
  format: { type: String, default: "yyyy-MM-dd" },
  monthPicker: { type: Boolean, default: false },
  yearPicker: { type: Boolean, default: false },
  placeholder: { type: String, default: "날짜 선택" },
  disabled: { type: Boolean, default: false },
  //locale: { type: Object, default: () => ko },
  locale: { type: String, default: "ko" },
});

const emit = defineEmits(["update:modelValue"]);
const isOpen = ref(false);

const parsedValue = computed(() => {
  if (!props.modelValue) return null;

  if (props.monthPicker) {
    if (typeof props.modelValue === "string") {
      const arrSplitData = props.modelValue.split("-");
      if (arrSplitData.length >= 2) {
        return {
          year: parseInt(arrSplitData[0], 10),
          month: parseInt(arrSplitData[1], 10) - 1,
        };
      }
    } else if (props.modelValue && typeof props.modelValue === "object") {
      if (
        props.modelValue.year !== undefined &&
        props.modelValue.month !== undefined
      ) {
        return props.modelValue;
      }
      if (props.modelValue instanceof Date && !isNaN(props.modelValue)) {
        return {
          year: props.modelValue.getFullYear(),
          month: props.modelValue.getMonth(),
        };
      }
    }
    return null;
  }

  if (props.yearPicker) {
    if (typeof props.modelValue === "object" && props.modelValue?.year) {
      return { year: parseInt(props.modelValue.year, 10) };
    }
    return { year: +props.modelValue };
  }

  if (props.modelValue instanceof Date) {
    return isNaN(props.modelValue) ? null : props.modelValue;
  }

  const d = new Date(props.modelValue);
  return isNaN(d.getTime()) ? null : d;
});

function onDateChange(v) {
  if (!v) {
    emit("update:modelValue", "");
    return;
  }

  let formatted = "";

  if (
    props.monthPicker &&
    v.year !== undefined &&
    v.month !== undefined &&
    v.month !== null
  ) {
    formatted = `${v.year}-${String(Number(v.month) + 1).padStart(2, "0")}`;
  } else if (props.yearPicker && v.year !== undefined && v.year !== null) {
    formatted = `${v.year}`;
  } else if (v instanceof Date && !isNaN(v)) {
    formatted = formatDate(v, props.format);
  } else if (typeof v === "string") {
    formatted = v;
  }

  emit("update:modelValue", formatted);
}
</script>
<!-- UCDate.vue 맨 아래에 추가 -->
<style>
/* Vue-Datepicker 인풋 높이 및 패딩을 py-1 / text-sm 규격으로 통일 */
.dp__input {
  padding-top: 0.25rem !important;    /* py-1 (4px) */
  padding-bottom: 0.25rem !important; /* py-1 (4px) */
  padding-left: 2rem !important;     /* 달력 아이콘 위치 확보 */
  font-size: 0.875rem !important;    /* text-sm (14px) */
  line-height: 1.25rem !important;
  border-color: #cbd5e1 !important;  /* border-blueGray-300 */
  border-radius: 0.25rem !important; /* rounded */
}

/* 달력 아이콘 위치 및 크기 정렬 */
.dp__input_icon {
  width: 0.875rem !important;
  height: 0.875rem !important;
  left: 0.6rem !important;
  padding: 0 !important;
}
</style>
